import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import GlassPanel from '@/components/ui/GlassPanel';
import BeamProgress from '@/components/ui/BeamProgress';
import { initialEnvelopes } from '@/data/mock';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';

const { FiPlus, FiX, FiCheck, FiLoader, FiTarget } = FiIcons;

export default function Envelopes() {
  const [envelopes, setEnvelopes] = useState(initialEnvelopes);
  const [isAdding, setIsAdding] = useState(false);
  const [formState, setFormState] = useState('idle'); // idle, loading, success
  const [newName, setNewName] = useState('');
  const [newTarget, setNewTarget] = useState('');

  const handleAdd = (e) => {
    e.preventDefault();
    if (!newName || !newTarget) return;

    setFormState('loading');
    
    // Simulate network/processing delay for realism
    setTimeout(() => {
      setFormState('success');
      setTimeout(() => {
        setEnvelopes([
          {
            id: Date.now(),
            name: newName,
            spent: 0,
            target: Number(newTarget),
            colorClass: 'from-bb-gold to-yellow-600'
          },
          ...envelopes
        ]);
        setFormState('idle');
        setIsAdding(false);
        setNewName('');
        setNewTarget('');
      }, 1000);
    }, 1500);
  };

  return (
    <div className="space-y-6 pb-24 md:pb-8 animate-in fade-in duration-500">
      
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
         <div>
           <h1 className="text-3xl font-bold text-white tracking-tight">Envelope Matrix</h1>
           <p className="text-gray-400 text-sm mt-1">Manage sector limits and monitor beam integrity.</p>
         </div>
         {!isAdding && (
           <button 
             onClick={() => setIsAdding(true)}
             className="flex items-center justify-center gap-2 bg-bb-gold/10 hover:bg-bb-gold/20 text-bb-gold border border-bb-gold/30 px-4 py-2 rounded-lg font-mono text-xs uppercase tracking-wider transition-all"
           >
             <SafeIcon icon={FiPlus} /> Initialize Beam
           </button>
         )}
      </div>

      <AnimatePresence>
        {isAdding && (
          <motion.div
            initial={{ opacity: 0, height: 0, y: -20 }}
            animate={{ opacity: 1, height: 'auto', y: 0 }}
            exit={{ opacity: 0, height: 0, y: -20 }}
            className="overflow-hidden relative z-20"
          >
            <GlassPanel className="p-6 border-bb-gold/30 shadow-[0_0_30px_rgba(212,175,55,0.05)]">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-sm text-bb-gold font-mono uppercase tracking-widest flex items-center">
                  <SafeIcon icon={FiTarget} className="mr-2" /> Configure New Sector
                </h3>
                <button onClick={() => setIsAdding(false)} className="text-gray-500 hover:text-white transition-colors">
                  <SafeIcon icon={FiX} size={20} />
                </button>
              </div>

              <form onSubmit={handleAdd} className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                <div className="md:col-span-6">
                  <label className="block text-[10px] text-gray-400 font-mono uppercase mb-2">Sector Designation</label>
                  <input 
                    type="text" 
                    required
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="e.g. Hyperdrive Maintenance" 
                    className="w-full bg-bb-dark border border-bb-glassBorder rounded-lg px-4 py-3 text-sm text-white focus:outline-none focus:border-bb-gold transition-colors font-sans"
                    disabled={formState !== 'idle'}
                  />
                </div>
                <div className="md:col-span-4">
                  <label className="block text-[10px] text-gray-400 font-mono uppercase mb-2">Target Liquidity ($)</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-mono">$</span>
                    <input 
                      type="number" 
                      required
                      min="1"
                      value={newTarget}
                      onChange={(e) => setNewTarget(e.target.value)}
                      placeholder="0.00" 
                      className="w-full bg-bb-dark border border-bb-glassBorder rounded-lg pl-8 pr-4 py-3 text-sm text-white font-mono focus:outline-none focus:border-bb-gold transition-colors"
                      disabled={formState !== 'idle'}
                    />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <button 
                    type="submit" 
                    disabled={formState !== 'idle'}
                    className="w-full h-[46px] bg-bb-gold text-bb-dark font-bold rounded-lg flex items-center justify-center hover:bg-yellow-400 transition-colors disabled:opacity-70"
                  >
                    {formState === 'idle' && <span className="text-xs uppercase font-mono tracking-wider">Engage</span>}
                    {formState === 'loading' && <SafeIcon icon={FiLoader} className="animate-spin" size={18} />}
                    {formState === 'success' && <SafeIcon icon={FiCheck} size={20} />}
                  </button>
                </div>
              </form>
            </GlassPanel>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {envelopes.map((env, i) => (
          <motion.div
            key={env.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
          >
            <GlassPanel hover className="p-6 h-full flex flex-col justify-center">
              <BeamProgress {...env} />
              <div className="mt-4 flex justify-between items-center text-xs border-t border-bb-glassBorder pt-4">
                <span className="text-gray-500 font-mono">ID: {env.id.toString().slice(-4)}</span>
                <button className="text-gray-400 hover:text-white transition-colors">Adjust Limits</button>
              </div>
            </GlassPanel>
          </motion.div>
        ))}
      </div>
    </div>
  );
}